import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { getIdToken } from "./amplify";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Verify from "./pages/Verify";
import Onboarding from "./pages/Onboarding";
import AppShell from "./layout/AppShell";
import Today from "./pages/app/Today";
import Journey from "./pages/app/Journey";
import Vocabulary from "./pages/app/Vocabulary";
import Personas from "./pages/app/Personas";
import CustomPersona from "./pages/app/CustomPersona";
import Profile from "./pages/app/Profile";
import Admin from "./pages/app/Admin";
import NotFound from "./pages/NotFound";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Status from "./pages/Status";
import CookieConsent from "./components/CookieConsent";
import Logo from "./components/common/Logo";

type AuthState = "checking" | "authed" | "anon";

function useAuthState(): AuthState {
  const [state, setState] = useState<AuthState>("checking");
  useEffect(() => {
    let cancelled = false;
    getIdToken().then((tok) => {
      if (cancelled) return;
      setState(tok ? "authed" : "anon");
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Logo size="lg" />
    </div>
  );
}

function Protected({ children }: { children: JSX.Element }) {
  const auth = useAuthState();
  if (auth === "checking") return <Loader />;
  if (auth === "anon") return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }: { children: JSX.Element }) {
  const auth = useAuthState();
  if (auth === "checking") return <Loader />;
  if (auth === "authed") return <Navigate to="/app/today" replace />;
  return children;
}

export default function App() {
  return (
    <>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/status" element={<Status />} />

      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<Navigate to="/app/today" replace />} />
        <Route path="today" element={<Today />} />
        <Route path="progress" element={<Navigate to="/app/journey" replace />} />
        <Route path="journey" element={<Journey />} />
        <Route path="vocabulary" element={<Vocabulary />} />
        <Route path="personas" element={<Personas />} />
        <Route path="personas/new" element={<CustomPersona />} />
        <Route path="profile" element={<Profile />} />
        <Route path="admin" element={<Admin />} />
      </Route>

      {/* Legacy redirect */}
      <Route path="/console" element={<Navigate to="/app/today" replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    <CookieConsent />
    </>
  );
}
